import React from "react";
import { Label, RadioGroup, RadioGroupItem } from "@jem-open/jem-ui";
import { Layers, MapPin, Users as UsersIcon } from "lucide-react";
import SearchableSelect, { SearchableSelectOption } from "./SearchableSelect";

export type ScopeKind = "site" | "roster" | "region";

interface ScopePickerProps {
  scopeKind: ScopeKind;
  onScopeKindChange: (v: ScopeKind) => void;
  selectedId: string | null;
  onSelectedIdChange: (v: string | null) => void;
  siteOptions: SearchableSelectOption[];
  rosterOptions: SearchableSelectOption[];
  regionOptions: SearchableSelectOption[];
  isLoading?: boolean;
}

const NOUN: Record<ScopeKind, string> = {
  site: "site",
  roster: "roster",
  region: "region",
};

const ScopePicker = ({
  scopeKind,
  onScopeKindChange,
  selectedId,
  onSelectedIdChange,
  siteOptions,
  rosterOptions,
  regionOptions,
  isLoading,
}: ScopePickerProps): JSX.Element => {
  const optionsByKind: Record<ScopeKind, SearchableSelectOption[]> = {
    site: siteOptions,
    roster: rosterOptions,
    region: regionOptions,
  };
  const options = optionsByKind[scopeKind];
  const noun = NOUN[scopeKind];

  let placeholder: string;
  if (isLoading) {
    placeholder = "Loading…";
  } else {
    placeholder = `Search ${noun}s…`;
  }

  let emptyMessage: string;
  if (isLoading) {
    emptyMessage = "Loading…";
  } else if (options.length === 0) {
    emptyMessage = `No ${noun}s available.`;
  } else {
    emptyMessage = `No ${noun}s match your search.`;
  }

  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-md space-y-md">
      <div>
        <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
          What should this report cover?
        </Label>
        <RadioGroup
          value={scopeKind}
          onValueChange={(v) => {
            onScopeKindChange(v as ScopeKind);
            onSelectedIdChange(null);
          }}
          className="flex flex-wrap items-center gap-md sm:gap-lg"
        >
          <div className="flex items-center gap-xs">
            <RadioGroupItem value="region" id="scope-region" />
            <Label
              htmlFor="scope-region"
              className="text-sm flex items-center gap-xxs"
            >
              <Layers size={14} className="text-greyscale-text-caption" />A
              region
            </Label>
          </div>
          <div className="flex items-center gap-xs">
            <RadioGroupItem value="site" id="scope-site" />
            <Label
              htmlFor="scope-site"
              className="text-sm flex items-center gap-xxs"
            >
              <MapPin size={14} className="text-greyscale-text-caption" />A site
            </Label>
          </div>
          <div className="flex items-center gap-xs">
            <RadioGroupItem value="roster" id="scope-roster" />
            <Label
              htmlFor="scope-roster"
              className="text-sm flex items-center gap-xxs"
            >
              <UsersIcon size={14} className="text-greyscale-text-caption" />A
              roster
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div>
        <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
          {`Pick a ${noun}`}
        </Label>
        <SearchableSelect
          className="w-full sm:max-w-[420px]"
          value={selectedId}
          onChange={onSelectedIdChange}
          placeholder={placeholder}
          emptyMessage={emptyMessage}
          options={options}
        />
      </div>
    </div>
  );
};

export default ScopePicker;
